import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "@/lib/api-fetch";
import {
  Users,
  Plus,
  X,
  Trash2,
  Pencil,
  Phone,
  Mail,
  MapPin,
  StickyNote,
  ArrowLeft,
  ChevronRight,
  FileText,
  Building2,
  User,
  CalendarDays,
  Truck,
  Package,
  Weight,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Client = {
  id: number;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
};

type ClientForm = {
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  notes: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Clients() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail panel state
  const [clientParams, setClientParams] = useSearchParams();
  const [selectedClient, setSelectedClientState] = useState<Client | null>(null);
  const setSelectedClient = (client: Client | null) => {
    setSelectedClientState(client);
    setClientParams(client ? { client: String(client.id) } : {});
  };
  useEffect(() => {
    const id = clientParams.get("client");
    setSelectedClientState(id ? clients.find(client => String(client.id) === id) || null : null);
  }, [clients, clientParams]);

  // Activity data
  const [activity, setActivity] = useState<{
    workOrders: any[];
    bols: any[];
    summary: { totalWos: number; totalBols: number; totalTons: number };
  } | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  // Link WO picker
  const [showWoPicker, setShowWoPicker] = useState(false);
  const [woSearch, setWoSearch] = useState("");
  const [availableWos, setAvailableWos] = useState<any[]>([]);
  const [loadingWos, setLoadingWos] = useState(false);

  // Link BOL picker
  const [showBolPicker, setShowBolPicker] = useState(false);
  const [bolSearch, setBolSearch] = useState("");
  const [availableBols, setAvailableBols] = useState<any[]>([]);
  const [loadingBols, setLoadingBols] = useState(false);

  // Modal state (add/edit)
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState<ClientForm>({
    name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    notes: "",
  });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/ops/clients", { headers });
      if (!res.ok) throw new Error(`Failed to load clients (${res.status})`);
      const data = await res.json();
      setClients(data.clients ?? data ?? []);
    } catch (err: any) {
      setError(err?.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch activity when client selected
  useEffect(() => {
    if (!selectedClient) {
      setActivity(null);
      return;
    }
    let cancelled = false;
    setLoadingActivity(true);
    setActivity(null);
    setActivityError(null);
    apiFetch(`/api/ops/clients/${selectedClient.id}/activity`, { headers })
      .then((res) => { if (!res.ok) throw new Error('Request failed'); return res.json(); })
      .then((data) => {
        if (!cancelled) setActivity(data);
      })
      .catch(() => { if (!cancelled) setActivityError("Could not load client activity. Reopen this client to retry."); })
      .finally(() => {
        if (!cancelled) setLoadingActivity(false);
      });
    return () => { cancelled = true; };
  }, [selectedClient, headers]);

  // -----------------------------------------------------------------------
  // Link Work Orders to Client
  // -----------------------------------------------------------------------

  const refreshActivity = async () => {
    if (!selectedClient) return;
    try {
      const res = await apiFetch(`/api/ops/clients/${selectedClient.id}/activity`, { headers });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      setActivity(data);
    } catch (e: any) { setActivityError(e.message || "Request failed. Please try again."); }
  };

  const openWoPicker = async () => {
    setShowWoPicker(true);
    setWoSearch("");
    setLoadingWos(true);
    try {
      const res = await apiFetch("/api/ops/work-orders", { headers });
      if (!res.ok) throw new Error("Could not load records.");
      const json = await res.json();
      const all = json.workOrders ?? json ?? [];
      const linkedIds = new Set((activity?.workOrders ?? []).map((w: any) => w.id));
      setAvailableWos(all.filter((w: any) => !linkedIds.has(w.id)));
    } catch (e: any) { setActivityError(e.message || "Request failed. Please try again."); }
    setLoadingWos(false);
  };

  const linkWo = async (woId: number) => {
    if (!selectedClient) return;
    try {
      const response = await apiFetch(`/api/ops/work-orders/${woId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ clientId: selectedClient.id, clientName: selectedClient.name }),
      });
      if (!response.ok) throw new Error("Could not link work order.");
      setAvailableWos((prev) => prev.filter((w) => w.id !== woId));
      await refreshActivity();
    } catch (e: any) { setActivityError(e.message || "Request failed. Please try again."); }
  };

  const openBolPicker = async () => {
    setShowBolPicker(true);
    setBolSearch("");
    setLoadingBols(true);
    try {
      const res = await apiFetch("/api/ops/bols", { headers });
      if (!res.ok) throw new Error("Could not load records.");
      const json = await res.json();
      const all = json.bols ?? json ?? [];
      const linkedIds = new Set((activity?.bols ?? []).map((b: any) => b.id));
      setAvailableBols(all.filter((b: any) => !linkedIds.has(b.id)));
    } catch (e: any) { setActivityError(e.message || "Request failed. Please try again."); }
    setLoadingBols(false);
  };

  const linkBol = async (bolId: number) => {
    if (!selectedClient) return;
    try {
      const response = await apiFetch(`/api/ops/bols/${bolId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ clientId: selectedClient.id, customerName: selectedClient.name, clientTag: selectedClient.name }),
      });
      if (!response.ok) throw new Error("Could not link BOL.");
      setAvailableBols((prev) => prev.filter((b) => b.id !== bolId));
      await refreshActivity();
    } catch (e: any) { setActivityError(e.message || "Request failed. Please try again."); }
  };

  // -----------------------------------------------------------------------
  // Modal handlers
  // -----------------------------------------------------------------------

  const openAdd = () => {
    setEditingClient(null);
    setForm({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", notes: "" });
    setShowModal(true);
  };

  const openEdit = (client: Client) => {
    setEditingClient(client);
    setForm({
      name: client.name,
      contact_name: client.contact_name || "",
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      address: client.address || "",
      notes: client.notes || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setForm({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", notes: "" });
  };

  // -----------------------------------------------------------------------
  // Save (create or update)
  // -----------------------------------------------------------------------

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        contactName: form.contact_name.trim() || null,
        contactEmail: form.contact_email.trim() || null,
        contactPhone: form.contact_phone.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };

      if (editingClient) {
        const res = await apiFetch(`/api/ops/clients/${editingClient.id}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const data = await res.json(); throw new Error(data.message || data.error || "Failed to update client"); }
        const updated = await res.json();
        // Update selected client if we're editing the currently viewed one
        if (selectedClient?.id === editingClient.id) {
          setSelectedClient(updated);
        }
      } else {
        const res = await apiFetch("/api/ops/clients", {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const data = await res.json(); throw new Error(data.message || data.error || "Failed to create client"); }
      }

      closeModal();
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Delete
  // -----------------------------------------------------------------------

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete client "${name}"?`)) return;
    try {
      const res = await apiFetch(`/api/ops/clients/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Failed to delete client");
      if (selectedClient?.id === id) setSelectedClient(null);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete client");
    }
  };

  // -----------------------------------------------------------------------
  // Client detail panel
  // -----------------------------------------------------------------------

  if (selectedClient) {
    return (
      <div className="space-y-6 max-w-5xl">
        {/* Back + header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedClient(null)}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-forest-600 text-white text-sm font-bold flex items-center justify-center">
                {initials(selectedClient.name)}
              </div>
              <div>
                <h1 className="heading-display text-2xl text-foreground">
                  {selectedClient.name}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Added {formatDate(selectedClient.created_at)}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openEdit(selectedClient)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
            >
              <Pencil size={15} />
              Edit
            </button>
            <button
              onClick={() => handleDelete(selectedClient.id, selectedClient.name)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 text-red-600 dark:text-red-400 text-sm font-semibold rounded-xl transition-colors"
            >
              <Trash2 size={15} />
              Delete
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Contact Info */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <User size={14} className="text-forest-600" />
              Contact Information
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">Contact Name</span>
                <p className="font-medium text-foreground mt-0.5">
                  {selectedClient.contact_name || <span className="text-muted-foreground/50">Not set</span>}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Email</span>
                <p className="font-medium text-foreground mt-0.5">
                  {selectedClient.contact_email ? (
                    <a href={`mailto:${selectedClient.contact_email}`} className="text-forest-600 dark:text-forest-400 hover:underline">
                      {selectedClient.contact_email}
                    </a>
                  ) : (
                    <span className="text-muted-foreground/50">Not set</span>
                  )}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Phone</span>
                <p className="font-medium text-foreground mt-0.5">
                  {selectedClient.contact_phone ? (
                    <a href={`tel:${selectedClient.contact_phone}`} className="text-forest-600 dark:text-forest-400 hover:underline">
                      {selectedClient.contact_phone}
                    </a>
                  ) : (
                    <span className="text-muted-foreground/50">Not set</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Address & Details */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Building2 size={14} className="text-forest-600" />
              Details
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">Address</span>
                <p className="font-medium text-foreground mt-0.5 whitespace-pre-wrap">
                  {selectedClient.address || <span className="text-muted-foreground/50">Not set</span>}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Created</span>
                <p className="font-medium text-foreground mt-0.5">
                  {formatDate(selectedClient.created_at)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Notes section */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <StickyNote size={14} className="text-forest-600" />
            Notes
          </h3>
          {selectedClient.notes ? (
            <p className="text-sm text-foreground whitespace-pre-wrap">{selectedClient.notes}</p>
          ) : (
            <p className="text-sm text-muted-foreground/50">No notes yet. Click Edit to add notes.</p>
          )}
        </div>

        {/* Activity Summary */}
        {activityError && <p role="alert" className="p-3 text-sm text-red-600">{activityError}</p>}
              {loadingActivity ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground text-sm">
            Loading activity...
          </div>
        ) : activity && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-foreground tabular-nums">{activity.summary.totalWos}</p>
                <p className="text-xs text-muted-foreground mt-1">Work Orders</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-foreground tabular-nums">{activity.summary.totalBols}</p>
                <p className="text-xs text-muted-foreground mt-1">BOLs</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-foreground tabular-nums">{activity.summary.totalTons.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Tons</p>
              </div>
            </div>

            {/* Work Orders */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Package size={14} className="text-forest-600" />
                  Work Orders
                  {activity.workOrders.length > 0 && (
                    <span className="text-xs font-medium bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 px-2 py-0.5 rounded-full">
                      {activity.workOrders.length}
                    </span>
                  )}
                </h3>
                <button
                  onClick={openWoPicker}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
                >
                  <Plus size={12} strokeWidth={2.5} />
                  Link WO
                </button>
              </div>

              {/* WO Picker */}
              {showWoPicker && (
                <div className="border border-border rounded-lg bg-secondary/30 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Select Work Order</span>
                    <button onClick={() => setShowWoPicker(false)} className="p-1 hover:bg-secondary rounded">
                      <X size={14} className="text-muted-foreground" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={woSearch}
                    onChange={(e) => setWoSearch(e.target.value)}
                    placeholder="Search by WO #, product, client..."
                    className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg"
                    autoFocus
                  />
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {loadingWos ? (
                      <p className="text-xs text-muted-foreground text-center py-3">Loading...</p>
                    ) : (() => {
                      const q = woSearch.toLowerCase();
                      const filtered = availableWos.filter((w: any) =>
                        !q ||
                        (w.wo_number || "").toLowerCase().includes(q) ||
                        (w.product_name || "").toLowerCase().includes(q) ||
                        (w.client_name || "").toLowerCase().includes(q)
                      );
                      return filtered.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-3">
                          {availableWos.length === 0 ? "No work orders available" : "No matches"}
                        </p>
                      ) : (
                        filtered.slice(0, 20).map((w: any) => (
                          <button
                            key={w.id}
                            onClick={() => linkWo(w.id)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-forest-50 dark:hover:bg-forest-900/20 rounded-lg transition-colors text-left"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-mono font-semibold text-foreground shrink-0">{w.wo_number}</span>
                              <span className="text-muted-foreground truncate">{w.product_name || ""}</span>
                            </div>
                            <Plus size={12} className="text-forest-600 shrink-0 ml-2" />
                          </button>
                        ))
                      );
                    })()}
                  </div>
                </div>
              )}

              {activity.workOrders.length === 0 && !showWoPicker ? (
                <p className="text-xs text-muted-foreground">No work orders found for this client.</p>
              ) : activity.workOrders.length > 0 ? (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">WO #</th>
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Product</th>
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                        <th className="text-right py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Qty</th>
                        <th className="text-center py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.workOrders.map((wo: any) => (
                        <tr
                          key={wo.id}
                          className="border-b border-border/50 hover:bg-secondary/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/app/ops/work-orders/${wo.id}`)}
                        >
                          <td className="py-2 px-2 font-mono font-semibold text-foreground">{wo.wo_number}</td>
                          <td className="py-2 px-2 text-muted-foreground truncate max-w-[160px]">{wo.product_name || "-"}</td>
                          <td className="py-2 px-2 text-muted-foreground">{wo.order_type || "-"}</td>
                          <td className="py-2 px-2 text-right font-semibold tabular-nums text-foreground">
                            {wo.quantity || "-"} {wo.quantity_type || ""}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              wo.status === "completed"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : wo.status === "in_progress"
                                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                            }`}>
                              {wo.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            {/* BOLs */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Truck size={14} className="text-forest-600" />
                  Bills of Lading
                  {activity.bols.length > 0 && (
                    <span className="text-xs font-medium bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 px-2 py-0.5 rounded-full">
                      {activity.bols.length}
                    </span>
                  )}
                </h3>
                <button
                  onClick={openBolPicker}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors"
                >
                  <Plus size={12} strokeWidth={2.5} />
                  Link BOL
                </button>
              </div>

              {/* BOL Picker */}
              {showBolPicker && (
                <div className="border border-border rounded-lg bg-secondary/30 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Select BOL to link</span>
                    <button onClick={() => setShowBolPicker(false)} className="p-1 hover:bg-secondary rounded">
                      <X size={14} className="text-muted-foreground" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={bolSearch}
                    onChange={(e) => setBolSearch(e.target.value)}
                    placeholder="Search by BOL #, customer, material..."
                    className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg"
                    autoFocus
                  />
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {loadingBols ? (
                      <p className="text-xs text-muted-foreground text-center py-3">Loading...</p>
                    ) : (() => {
                      const q = bolSearch.toLowerCase();
                      const filtered = availableBols.filter((b: any) =>
                        !q ||
                        (b.bol_number || "").toLowerCase().includes(q) ||
                        (b.customer_name || "").toLowerCase().includes(q) ||
                        (b.material_type || "").toLowerCase().includes(q)
                      );
                      return filtered.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-3">
                          {availableBols.length === 0 ? "No BOLs available" : "No matches"}
                        </p>
                      ) : (
                        filtered.slice(0, 30).map((b: any) => (
                          <button
                            key={b.id}
                            onClick={() => linkBol(b.id)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-forest-50 dark:hover:bg-forest-900/20 rounded-lg transition-colors text-left"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-mono font-semibold text-foreground shrink-0">{b.bol_number}</span>
                              <span className="text-muted-foreground truncate">
                                {b.customer_name || ""}{b.customer_name && b.material_type ? " — " : ""}{b.material_type || ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              <span className="tabular-nums font-semibold text-foreground">{Number(b.weight_tons || b.net_weight_tons || 0).toFixed(2)}t</span>
                              <Plus size={12} className="text-forest-600" />
                            </div>
                          </button>
                        ))
                      );
                    })()}
                  </div>
                </div>
              )}

              {activity.bols.length === 0 && !showBolPicker ? (
                <p className="text-xs text-muted-foreground">No BOLs found for this client.</p>
              ) : activity.bols.length > 0 ? (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">BOL #</th>
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                        <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Material</th>
                        <th className="text-right py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Tons</th>
                        <th className="text-center py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.bols.map((b: any) => (
                        <tr
                          key={b.id}
                          className="border-b border-border/50 hover:bg-secondary/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/app/ops/bols/${b.id}`)}
                        >
                          <td className="py-2 px-2 font-mono font-semibold text-foreground">{b.bol_number}</td>
                          <td className="py-2 px-2 text-muted-foreground">
                            {b.date ? new Date(b.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-"}
                          </td>
                          <td className="py-2 px-2 text-muted-foreground truncate max-w-[140px]">{b.material_type || "-"}</td>
                          <td className="py-2 px-2 text-right font-semibold tabular-nums text-foreground">
                            {Number(b.net_weight_tons || 0).toFixed(2)}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              b.status === "delivered"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                            }`}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </>
        )}

        {/* Edit Modal (rendered when editing from detail view) */}
        {renderModal()}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Modal JSX (shared between list and detail views)
  // -----------------------------------------------------------------------

  function renderModal() {
    if (!showModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/30" onClick={closeModal} />
        <div className="relative w-full max-w-md bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl overflow-y-auto">
          <form onSubmit={handleSave} className="p-6 space-y-5">
            {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingClient ? "Edit Client" : "Add Client"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
              >
                <X size={18} />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Client Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Corporation"
                required
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Contact Name
              </label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="John Smith"
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Contact Email
              </label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="john@example.com"
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Contact Phone
              </label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="(602) 555-0000"
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Address
              </label>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-3 text-gray-400" />
                <textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="123 Main St, Phoenix, AZ 85001"
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Notes
              </label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional information about this client..."
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving || !form.name.trim()}
                className="flex-1 px-4 py-2.5 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                {saving ? "Saving..." : editingClient ? "Save Changes" : "Add Client"}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render - Client list
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-display text-3xl text-foreground">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage operations clients
          </p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
        >
          <Plus size={16} strokeWidth={2.5} /> Add Client
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Client list */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading clients...</div>
        ) : clients.length === 0 ? (
          <div className="p-16 text-center">
            <Users size={40} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No clients yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Add your first client to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3 hidden md:table-cell">Email</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Phone</th>
                  <th className="px-4 py-3 hidden xl:table-cell">Notes</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => setSelectedClient(client)}
                    className="border-b border-border/60 last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-forest-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                          {initials(client.name)}
                        </div>
                        <span className="font-semibold text-forest-600 dark:text-forest-400 hover:underline">{client.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {client.contact_name || <span className="text-muted-foreground/40">-</span>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {client.contact_email ? (
                        <span className="text-forest-600 dark:text-forest-400">
                          {client.contact_email}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">
                      {client.contact_phone || <span className="text-muted-foreground/40">-</span>}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell max-w-[250px]">
                      {client.notes ? (
                        <span className="text-muted-foreground truncate block">{client.notes}</span>
                      ) : (
                        <span className="text-muted-foreground/40">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(client); }}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(client.id, client.name); }}
                          className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                        <ChevronRight size={15} className="text-muted-foreground/40 ml-1" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {renderModal()}
    </div>
  );
}
